import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle,
  AlertTriangle,
  Clipboard,
  ClipboardCheck,
  Lock,
  EyeOff,
  Mail,
  Smartphone,
  Key,
} from 'lucide-react';

interface CredentialData {
  userName: string;
  userEmail: string;
  tempPassword: string;
  licenseName?: string;
  clientName: string;
  createdAt: string;
}

interface StatusData {
  status: 'pending_verification';
  clientName: string;
  emailHint: string;
  verificationMethod?: 'email' | 'sms';
  phoneHint?: string;
}

type Step = 'loading' | 'verify' | 'code' | 'credentials' | 'error';

export default function ViewCredentials() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [alreadyViewed, setAlreadyViewed] = useState(false);
  const [statusData, setStatusData] = useState<StatusData | null>(null);
  const [credentials, setCredentials] = useState<CredentialData | null>(null);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [maskedContact, setMaskedContact] = useState<string | null>(null);

  const apiBase = import.meta.env.VITE_API_URL || 'https://pax8lic-api.azurewebsites.net/api';

  useEffect(() => {
    if (token) {
      checkStatus();
    }
  }, [token]);

  const checkStatus = async () => {
    try {
      const response = await fetch(`${apiBase}/secure-credentials/${token}`);
      const data = await response.json();

      if (data.success && data.status === 'pending_verification') {
        setStatusData(data);
        setStep('verify');
      } else if (data.expired) {
        setExpired(true);
        setError(data.error);
        setStep('error');
      } else if (data.alreadyViewed) {
        setAlreadyViewed(true);
        setError(data.error);
        setStep('error');
      } else {
        setError(data.error || 'Unknown error');
        setStep('error');
      }
    } catch (err) {
      setError('Failed to load credentials. Please try again.');
      setStep('error');
    }
  };

  const isSmsVerification = statusData?.verificationMethod === 'sms';

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);

    try {
      const bodyPayload = isSmsVerification
        ? { phone: phone.replace(/\D/g, '') }
        : { email: email.trim().toLowerCase() };

      const response = await fetch(`${apiBase}/secure-credentials/${token}/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload),
      });

      const data = await response.json();

      if (data.success) {
        setMaskedContact(data.maskedPhone || data.maskedEmail || (isSmsVerification ? phone : email));
        setStep('code');
      } else {
        setFormError(data.error || 'Failed to send verification code');
        if (data.expired || data.alreadyViewed) {
          setExpired(data.expired || false);
          setAlreadyViewed(data.alreadyViewed || false);
          setError(data.error);
          setStep('error');
        }
      }
    } catch (err) {
      setFormError('Failed to send verification code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);

    try {
      const response = await fetch(`${apiBase}/secure-credentials/${token}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setCredentials(data);
        setStep('credentials');
      } else {
        setFormError(data.error || 'Invalid verification code');
        if (data.expired || data.alreadyViewed || data.tooManyAttempts) {
          setExpired(data.expired || false);
          setAlreadyViewed(data.alreadyViewed || data.tooManyAttempts || false);
          setError(data.error);
          setStep('error');
        }
      }
    } catch (err) {
      setFormError('Failed to verify code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!credentials) return;

    const text = `Account Credentials for ${credentials.clientName}

Name: ${credentials.userName}
Email/Username: ${credentials.userEmail}
Temporary Password: ${credentials.tempPassword}
${credentials.licenseName ? `License: ${credentials.licenseName}` : ''}

Sign in at: https://portal.office.com
Note: You will be prompted to change your password on first sign-in.`;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {alreadyViewed ? (
              <EyeOff className="h-8 w-8 text-red-600" />
            ) : (
              <AlertTriangle className="h-8 w-8 text-red-600" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {alreadyViewed ? 'Already Viewed' : expired ? 'Link Expired' : 'Error'}
          </h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          {alreadyViewed && (
            <p className="text-sm text-muted-foreground">
              For security reasons, credential links can only be viewed once.
              Please contact your IT administrator if you need your credentials resent.
            </p>
          )}
          {expired && (
            <p className="text-sm text-muted-foreground">
              This link has expired. Please contact your IT administrator to receive a new link.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Verification step (SMS or email)
  if (step === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 p-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-t-2xl p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Verify Your Identity</h1>
            <p className="text-primary-100 mt-1">Welcome to {statusData?.clientName}</p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-b-2xl shadow-xl p-6">
            {isSmsVerification ? (
              /* SMS Verification */
              <div className="text-center mb-6">
                <Smartphone className="h-12 w-12 text-primary-600 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Confirm your phone number to receive a verification code via SMS.
                </p>
                {statusData?.phoneHint && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Phone on file: {statusData.phoneHint}
                  </p>
                )}
              </div>
            ) : (
              /* Email Verification */
              <div className="text-center mb-6">
                <Mail className="h-12 w-12 text-primary-600 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Enter your email address to receive a verification code.
                </p>
                {statusData?.emailHint && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Hint: {statusData.emailHint}
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleSendCode}>
              <div className="mb-4">
                {isSmsVerification ? (
                  <>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      required
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </>
                ) : (
                  <>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </>
                )}
              </div>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || (isSmsVerification ? !phone : !email)}
                className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              {isSmsVerification
                ? 'A 6-digit code will be sent to your phone via SMS.'
                : 'A 6-digit code will be sent to your email.'}
            </p>
          </div>

          {/* Branding */}
          <p className="text-center text-xs text-muted-foreground mt-4">
            Secure credentials provided by Umetech, Inc.
          </p>
        </div>
      </div>
    );
  }

  // Code verification step
  if (step === 'code') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 p-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-t-2xl p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Enter Verification Code</h1>
            <p className="text-primary-100 mt-1">{statusData?.clientName}</p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-b-2xl shadow-xl p-6">
            <div className="text-center mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-green-700 text-sm">
                  Verification code sent {isSmsVerification ? 'via SMS' : ''} to <strong>{maskedContact}</strong>
                </p>
              </div>
              <p className="text-muted-foreground">
                Enter the 6-digit code from your {isSmsVerification ? 'text message' : 'email'}.
              </p>
            </div>

            <form onSubmit={handleVerifyCode}>
              <div className="mb-4">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  maxLength={6}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-widest font-mono"
                />
              </div>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || code.length !== 6}
                className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setStep('verify');
                  setCode('');
                  setFormError(null);
                }}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Didn't receive the code? Try again
              </button>
            </div>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              Code expires in 10 minutes.
            </p>
          </div>

          {/* Branding */}
          <p className="text-center text-xs text-muted-foreground mt-4">
            Secure credentials provided by Umetech, Inc.
          </p>
        </div>
      </div>
    );
  }

  // Credentials display step
  if (!credentials) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 p-4">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-t-2xl p-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold">Your Account Credentials</h1>
          <p className="text-primary-100 mt-1">Welcome to {credentials.clientName}</p>
        </div>

        {/* Credentials Card */}
        <div className="bg-white rounded-b-2xl shadow-xl p-6">
          {/* Security Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Save Your Credentials</p>
                <p>Please save this information now. This page will not be accessible again.</p>
              </div>
            </div>
          </div>

          {/* Credentials */}
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</label>
              <p className="text-lg text-foreground font-medium">{credentials.userName}</p>
            </div>

            <div className="border border-border rounded-lg p-4">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email / Username</label>
              <p className="text-lg text-foreground font-medium break-all">{credentials.userEmail}</p>
            </div>

            <div className="border border-border rounded-lg p-4 bg-amber-50 border-amber-200">
              <label className="text-xs font-medium text-amber-700 uppercase tracking-wide">Temporary Password</label>
              <div className="flex items-center justify-between mt-1">
                <p className="text-lg text-foreground font-mono font-bold">
                  {showPassword ? credentials.tempPassword : '••••••••••••'}
                </p>
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {credentials.licenseName && (
              <div className="border border-border rounded-lg p-4">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">License Assigned</label>
                <p className="text-lg text-foreground font-medium">{credentials.licenseName}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 space-y-3">
            <button
              onClick={handleCopy}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                copied
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : 'bg-muted text-foreground hover:bg-accent border border-border'
              }`}
            >
              {copied ? (
                <>
                  <ClipboardCheck className="h-5 w-5" />
                  Copied to Clipboard!
                </>
              ) : (
                <>
                  <Clipboard className="h-5 w-5" />
                  Copy All Credentials
                </>
              )}
            </button>

            <a
              href="https://portal.office.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-primary-600 text-white hover:bg-primary-700 transition-all"
            >
              <CheckCircle className="h-5 w-5" />
              Sign In to Microsoft 365
            </a>
          </div>

          {/* Footer Note */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>You will be prompted to change your password on first sign-in.</p>
            <p className="mt-2">Need help? Contact your IT administrator.</p>
          </div>
        </div>

        {/* Branding */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          Secure credentials provided by Umetech, Inc.
        </p>
      </div>
    </div>
  );
}
